import { type Component, createResource, createSignal, For, type JSX, Show } from 'solid-js'
import Accordion from '~/components/Accordion'
import AccordionGroup from '~/components/AccordionGroup'
import Alert from '~/components/Alert'
import Avatar from '~/components/Avatar'
import Breadcrumbs from '~/components/Breadcrumbs'
import Button from '~/components/Button'
import Card from '~/components/Card'
import Drawer from '~/components/Drawer'
import Gallery from '~/components/Gallery'
import Icon from '~/components/Icon'
import ImageViewer from '~/components/ImageViewer'
import List from '~/components/List'
import ListItem from '~/components/ListItem'
import ListItemIcon from '~/components/ListItemIcon'
import ListItemText from '~/components/ListItemText'
import Markdown from '~/components/Markdown'
import Modal from '~/components/Modal'
import Tabs from '~/components/Tabs'
import Tags from '~/components/Tags'
import FormRenderer from '~/layers/FormRenderer'
import { formService } from '~/services/form.service'
import { type ColumnType, embedUrl, type PageContentType, type SectionType, type WidgetType } from '~/services/page.service'
import type { AlertVariantType } from '~/types/Alert'
import { resolveError } from '~/utils/error'

/** En el editor los enlaces no navegan: se está diseñando, no visitando. */
const openLink = (href: string | undefined, newTab: boolean | undefined, interactive: boolean) => {
  if (!interactive || !href) return
  window.open(href, newTab ? '_blank' : '_self', newTab ? 'noopener' : '')
}

/**
 * Pinta una página del constructor. Lo usan el lienzo del editor, la
 * previsualización y la página pública, así que lo que se diseña es exactamente
 * lo que se publica.
 */

const PADDING_Y: Record<string, string> = {
  none: 'py-0',
  sm: 'py-6',
  md: 'py-10',
  lg: 'py-16',
  xl: 'py-24'
}

const BACKGROUND: Record<string, string> = {
  none: '',
  surface: 'bg-surface',
  muted: 'bg-surface-muted',
  primary: 'bg-primary text-white',
  dark: 'bg-slate-900 text-white'
}

const SECTION_WIDTH: Record<string, string> = {
  boxed: 'max-w-5xl',
  wide: 'max-w-7xl',
  full: 'max-w-none'
}

const GAP: Record<string, string> = { sm: 'gap-3', md: 'gap-6', lg: 'gap-10' }

const ALIGN: Record<string, string> = { left: 'text-left', center: 'text-center', right: 'text-right' }

const JUSTIFY: Record<string, string> = { left: 'justify-start', center: 'justify-center', right: 'justify-end' }

const HEADING_SIZE: Record<number, string> = {
  1: 'text-3xl sm:text-5xl font-bold',
  2: 'text-2xl sm:text-4xl font-bold',
  3: 'text-xl sm:text-2xl font-semibold',
  4: 'text-lg sm:text-xl font-semibold'
}

const TEXT_SIZE: Record<string, string> = { sm: 'text-sm', md: 'text-base', lg: 'text-lg' }

const TONE: Record<string, string> = { default: 'text-foreground', muted: 'text-foreground-subtle', primary: 'text-primary' }

const SPACER: Record<string, string> = { sm: 'h-4', md: 'h-10', lg: 'h-20', xl: 'h-32' }

const GALLERY_COLUMNS: Record<number, string> = {
  2: 'grid-cols-1 sm:grid-cols-2',
  3: 'grid-cols-2 sm:grid-cols-3',
  4: 'grid-cols-2 sm:grid-cols-4'
}

/** El ancho de columna es una fracción de 12; en móvil todas ocupan el ancho. */
const COLUMN_SPAN: Record<number, string> = {
  1: 'sm:col-span-1',
  2: 'sm:col-span-2',
  3: 'sm:col-span-3',
  4: 'sm:col-span-4',
  5: 'sm:col-span-5',
  6: 'sm:col-span-6',
  7: 'sm:col-span-7',
  8: 'sm:col-span-8',
  9: 'sm:col-span-9',
  10: 'sm:col-span-10',
  11: 'sm:col-span-11',
  12: 'sm:col-span-12'
}

/**
 * Formulario incrustado. Se pide por su slug público, igual que la página del
 * formulario, y envía por el mismo endpoint: un formulario dentro de una página
 * guarda respuestas exactamente igual que uno compartido por enlace.
 */
const EmbeddedForm: Component<{ widget: WidgetType; interactive: boolean }> = props => {
  const slug = () => props.widget.props.formSlug ?? ''

  const [form] = createResource(
    () => slug(),
    async (value: string) => {
      try {
        return value ? await formService.getPublic(value) : null
      } catch {
        return null
      }
    }
  )

  const [sending, setSending] = createSignal(false)
  const [sent, setSent] = createSignal<string | null>(null)
  const [globalError, setGlobalError] = createSignal('')
  const [serverErrors, setServerErrors] = createSignal<Record<string, string>>({})

  const handleSubmit = async (answers: Record<string, unknown>) => {
    setGlobalError('')
    setServerErrors({})
    setSending(true)
    try {
      const result = await formService.submit(slug(), answers)
      setSent(result.successMessage || '¡Gracias! Tu respuesta quedó registrada.')
    } catch (err: unknown) {
      const fieldErrors = (err as { response?: { data?: { errors?: Record<string, string> } } })?.response?.data?.errors
      if (fieldErrors && typeof fieldErrors === 'object') {
        setServerErrors(fieldErrors)
        setGlobalError('Revisa los campos marcados.')
      } else {
        setGlobalError(resolveError(err))
      }
    } finally {
      setSending(false)
    }
  }

  return (
    <Show
      when={slug()}
      fallback={
        <div class="rounded-xl border border-dashed border-border p-6 text-center text-sm text-foreground-subtle">
          <Icon icon="material-symbols-light:list-alt-outline" size={28} class="mx-auto mb-2" />
          Elige el formulario que quieres mostrar aquí.
        </div>
      }
    >
      <Card>
        <Show when={props.widget.props.showFormTitle !== false && (props.widget.props.formTitle || form()?.title)}>
          <Card.Header>
            <div>
              <h3 class="text-lg font-semibold text-foreground">{props.widget.props.formTitle || form()?.title}</h3>
              <Show when={form()?.description}>
                <p class="text-sm text-foreground-subtle mt-1 whitespace-pre-line">{form()?.description}</p>
              </Show>
            </div>
          </Card.Header>
        </Show>
        <Card.Body>
          <Show when={form.loading}>
            <p class="py-6 text-center text-sm text-foreground-subtle">Cargando formulario...</p>
          </Show>
          <Show when={form.error}>
            <p class="py-6 text-center text-sm text-danger">Este formulario no está disponible. Revisa que siga publicado.</p>
          </Show>
          <Show when={form() && !form.error}>
            <Show
              when={!sent()}
              fallback={
                <div class="py-8 text-center">
                  <Icon icon="material-symbols-light:check-circle-outline" size={44} class="mx-auto mb-2 text-success" />
                  <p class="text-sm text-foreground whitespace-pre-line">{sent()}</p>
                </div>
              }
            >
              <FormRenderer
                fields={form()!.fields ?? []}
                steps={form()!.steps}
                settings={form()!.settings}
                serverErrors={serverErrors()}
                globalError={globalError()}
                submitting={sending()}
                // En el editor se ve y se recorre, pero no se envía nada.
                preview={!props.interactive}
                onSubmit={handleSubmit}
              />
            </Show>
          </Show>
        </Card.Body>
      </Card>
    </Show>
  )
}

/**
 * Galería con el componente de la librería: la rejilla es la suya y al pulsar
 * una foto se abre el visor con zoom, igual que en el resto de la aplicación.
 */
const GalleryWidget: Component<{ widget: WidgetType; interactive: boolean }> = props => {
  const [viewerAt, setViewerAt] = createSignal<number | null>(null)
  const images = () => (props.widget.props.images ?? []).map(image => ({ src: image.src, alt: image.alt, caption: image.caption }))
  const columns = () => props.widget.props.columns ?? 3

  return (
    <>
      <Gallery images={images()} class={GALLERY_COLUMNS[columns()]} onClick={index => setViewerAt(index)} />
      {/* El visor se monta al abrirlo: si vive siempre, se queda con el índice
          de la primera vez y todas las fotos abrían en la número uno. */}
      <Show when={viewerAt() !== null}>
        <ImageViewer images={images()} open initialIndex={viewerAt() ?? 0} onClose={() => setViewerAt(null)} showThumbnails loop />
      </Show>
    </>
  )
}

/**
 * Botón que abre una ventana modal o un panel lateral con el contenido que se
 * haya escrito. En el lienzo también se puede abrir, para ver cómo queda.
 */
const OverlayWidget: Component<{ widget: WidgetType; interactive: boolean; kind: 'modal' | 'drawer' }> = props => {
  const [open, setOpen] = createSignal(false)
  const p = () => props.widget.props

  const body = () => (
    <div class="space-y-3 p-6">
      <p class="text-sm text-foreground-subtle whitespace-pre-line">{p().text}</p>
      <For each={p().links ?? []}>
        {link => (
          <Button size="sm" variant="text" color="info" fullWidth onClick={() => openLink(link.href, false, props.interactive)}>
            {link.label}
          </Button>
        )}
      </For>
    </div>
  )

  return (
    <>
      <div class={`flex ${JUSTIFY[p().align ?? 'left']}`}>
        <Button size={p().buttonSize ?? 'md'} color={p().color ?? 'primary'} variant={p().variant ?? 'contained'} onClick={() => setOpen(true)}>
          {p().label || 'Abrir'}
        </Button>
      </div>

      <Show when={props.kind === 'modal'}>
        <Modal isOpen={open()} onClose={() => setOpen(false)} size={p().panelSize ?? 'md'}>
          <Modal.Header>
            <h3 class="text-lg font-semibold text-foreground">{p().title}</h3>
          </Modal.Header>
          <Modal.Body>{body()}</Modal.Body>
        </Modal>
      </Show>

      <Show when={props.kind === 'drawer'}>
        <Drawer isOpen={open()} onClose={() => setOpen(false)} position={p().position ?? 'right'} size={p().panelSize ?? 'sm'} title={p().title}>
          {body()}
        </Drawer>
      </Show>
    </>
  )
}

/** Pinta un widget suelto. El lienzo del editor lo usa dentro de sus tarjetas. */
export const WidgetPreview: Component<{ widget: WidgetType; interactive: boolean }> = props => {
  const p = () => props.widget.props

  return (
    <>
      <Show when={props.widget.type === 'heading'}>
        {(() => {
          const level = p().level ?? 2
          return (
            <div class={ALIGN[p().align ?? 'left']}>
              <p class={`${HEADING_SIZE[level] ?? HEADING_SIZE[2]} ${TONE[p().tone ?? 'default']} leading-tight`}>{p().text}</p>
            </div>
          )
        })()}
      </Show>

      <Show when={props.widget.type === 'text'}>
        <p class={`${TEXT_SIZE[p().size ?? 'md']} ${TONE[p().tone ?? 'muted']} ${ALIGN[p().align ?? 'left']} whitespace-pre-line leading-relaxed`}>{p().text}</p>
      </Show>

      <Show when={props.widget.type === 'features'}>
        <div class={`grid gap-6 ${GALLERY_COLUMNS[p().columns ?? 3]}`}>
          <For each={p().items ?? []}>
            {item => (
              <div class="space-y-2">
                <Show when={item.icon}>
                  <Icon icon={item.icon!} size={30} class="text-primary" />
                </Show>
                <h4 class="font-semibold text-foreground">{item.title}</h4>
                <p class="text-sm text-foreground-subtle whitespace-pre-line">{item.text}</p>
              </div>
            )}
          </For>
        </div>
      </Show>

      <Show when={props.widget.type === 'testimonials'}>
        <div class={`grid gap-4 ${GALLERY_COLUMNS[p().columns ?? 3]}`}>
          <For each={p().quotes ?? []}>
            {quote => (
              <figure class="flex h-full flex-col justify-between rounded-xl border border-border bg-surface p-5">
                <Show when={(quote.rating ?? 0) > 0}>
                  <div class="mb-2 flex gap-0.5">
                    <For each={Array.from({ length: Math.min(5, quote.rating ?? 0) })}>{() => <Icon icon="material-symbols-light:star" size={18} class="text-warning" />}</For>
                  </div>
                </Show>
                <blockquote class="text-sm text-foreground whitespace-pre-line">“{quote.text}”</blockquote>
                <figcaption class="mt-4 flex items-center gap-3">
                  <Show
                    when={quote.avatar}
                    fallback={
                      <span class="flex size-9 items-center justify-center rounded-full bg-primary-soft text-sm font-semibold text-primary">
                        {(quote.author ?? '?').trim().charAt(0).toUpperCase()}
                      </span>
                    }
                  >
                    <img src={quote.avatar} alt={quote.author ?? ''} class="size-9 rounded-full object-cover" />
                  </Show>
                  <span>
                    <span class="block text-sm font-semibold text-foreground">{quote.author}</span>
                    <span class="block text-xs text-foreground-subtle">{quote.role}</span>
                  </span>
                </figcaption>
              </figure>
            )}
          </For>
        </div>
      </Show>

      <Show when={props.widget.type === 'image'}>
        <Show
          when={p().src}
          fallback={
            <div class="rounded-xl border border-dashed border-border py-10 text-center text-sm text-foreground-subtle">
              <Icon icon="material-symbols-light:image-outline" size={28} class="mx-auto mb-2" />
              Añade la imagen desde el panel de la derecha.
            </div>
          }
        >
          <div class={`flex ${JUSTIFY[p().align ?? 'center']}`}>
            <img src={p().src} alt={p().alt ?? ''} class={`max-w-full h-auto ${p().rounded === false ? '' : 'rounded-xl'}`} style={p().maxWidth ? { 'max-width': `${p().maxWidth}px` } : undefined} />
          </div>
        </Show>
      </Show>

      <Show when={props.widget.type === 'gallery'}>
        <Show
          when={(p().images ?? []).length > 0}
          fallback={
            <div class="rounded-xl border border-dashed border-border py-10 text-center text-sm text-foreground-subtle">
              <Icon icon="material-symbols-light:gallery-thumbnail-outline" size={28} class="mx-auto mb-2" />
              Sube imágenes para armar la galería.
            </div>
          }
        >
          <GalleryWidget widget={props.widget} interactive={props.interactive} />
        </Show>
      </Show>

      <Show when={props.widget.type === 'video'}>
        <Show
          when={embedUrl(p().url ?? '')}
          fallback={
            <div class="rounded-xl border border-dashed border-border py-10 text-center text-sm text-foreground-subtle">
              <Icon icon="material-symbols-light:play-circle-outline" size={28} class="mx-auto mb-2" />
              Pega el enlace de YouTube o Vimeo.
            </div>
          }
        >
          <div class="aspect-video w-full overflow-hidden rounded-xl bg-slate-900">
            <iframe
              src={embedUrl(p().url ?? '')}
              title={p().alt ?? 'Video'}
              class="h-full w-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen
            />
          </div>
        </Show>
      </Show>

      <Show when={props.widget.type === 'button'}>
        <div class={`flex ${JUSTIFY[p().align ?? 'left']}`}>
          <Button
            size={p().buttonSize ?? 'md'}
            color={p().color ?? 'primary'}
            variant={p().variant ?? 'contained'}
            onClick={() => {
              if (!props.interactive) return
              const href = p().href
              if (href) window.open(href, p().newTab ? '_blank' : '_self', p().newTab ? 'noopener' : '')
            }}
          >
            {p().label || 'Botón'}
          </Button>
        </div>
      </Show>

      <Show when={props.widget.type === 'form'}>
        <EmbeddedForm widget={props.widget} interactive={props.interactive} />
      </Show>

      <Show when={props.widget.type === 'markdown'}>
        <Markdown content={p().text ?? ''} headings />
      </Show>

      <Show when={props.widget.type === 'card'}>
        <Card color={p().color ?? 'primary'} style="border">
          <Show when={p().src}>
            <img src={p().src} alt={p().alt ?? ''} class="h-44 w-full rounded-t-xl object-cover" />
          </Show>
          <Card.Header>
            <h3 class="font-semibold text-foreground">{p().title}</h3>
          </Card.Header>
          <Card.Body>
            <p class="text-sm text-foreground-subtle whitespace-pre-line">{p().text}</p>
            <Show when={p().label}>
              <div class="mt-4">
                <Button size="sm" color={p().color ?? 'primary'} variant={p().variant ?? 'soft'} onClick={() => openLink(p().href, p().newTab, props.interactive)}>
                  {p().label}
                </Button>
              </div>
            </Show>
          </Card.Body>
        </Card>
      </Show>

      <Show when={props.widget.type === 'alert'}>
        <Alert variant={(p().color ?? 'info') as AlertVariantType} title={p().title} icon={p().icon} outlined={p().outlined}>
          <span class="whitespace-pre-line">{p().text}</span>
        </Alert>
      </Show>

      <Show when={props.widget.type === 'accordion'}>
        <AccordionGroup divider>
          <For each={p().items ?? []}>
            {(item, index) => (
              <Accordion title={item.title ?? ''} value={String(index())} icon={item.icon}>
                <p class="text-sm text-foreground-subtle whitespace-pre-line">{item.text}</p>
              </Accordion>
            )}
          </For>
        </AccordionGroup>
      </Show>

      <Show when={props.widget.type === 'tabs'}>
        <Tabs
          variant={p().tabsVariant ?? 'underline'}
          showContent
          items={(p().items ?? []).map((item, index) => ({
            id: String(index),
            label: item.title ?? `Pestaña ${index + 1}`,
            icon: item.icon,
            content: <p class="pt-3 text-sm text-foreground-subtle whitespace-pre-line">{item.text}</p>
          }))}
        />
      </Show>

      <Show when={props.widget.type === 'list'}>
        <List divided>
          <For each={p().items ?? []}>
            {item => (
              <ListItem>
                <Show when={item.icon}>
                  <ListItemIcon>
                    <Icon icon={item.icon!} size={22} class="text-primary" />
                  </ListItemIcon>
                </Show>
                <ListItemText primary={item.title} secondary={item.text} />
              </ListItem>
            )}
          </For>
        </List>
      </Show>

      <Show when={props.widget.type === 'chips'}>
        <div class={`flex flex-wrap gap-2 ${JUSTIFY[p().align ?? 'left']}`}>
          <Tags tags={p().tags ?? []} color={p().color ?? 'primary'} outlined={p().outlined} />
        </div>
      </Show>

      <Show when={props.widget.type === 'profile'}>
        <div class={`flex flex-col items-center gap-2 ${p().align === 'left' ? 'sm:items-start' : p().align === 'right' ? 'sm:items-end' : ''}`}>
          <Show
            when={p().src}
            fallback={
              <span class="flex size-20 items-center justify-center rounded-full bg-primary-soft text-2xl font-semibold text-primary">{(p().title ?? '?').trim().charAt(0).toUpperCase()}</span>
            }
          >
            <Avatar src={p().src} alt={p().title ?? ''} size={80} rounded="full" />
          </Show>
          <div class={ALIGN[p().align ?? 'center']}>
            <p class="font-semibold text-foreground">{p().title}</p>
            <p class="text-sm text-foreground-subtle">{p().text}</p>
          </div>
        </div>
      </Show>

      <Show when={props.widget.type === 'table'}>
        <div class="overflow-x-auto rounded-xl border border-border">
          <table class="w-full text-sm">
            <thead class="bg-surface-muted">
              <tr>
                <For each={p().headers ?? []}>{header => <th class="px-4 py-2.5 text-left font-semibold text-foreground whitespace-nowrap">{header}</th>}</For>
              </tr>
            </thead>
            <tbody>
              <For each={p().rows ?? []}>
                {(row, index) => (
                  <tr class={p().striped !== false && index() % 2 === 1 ? 'bg-surface-muted/50' : ''}>
                    <For each={row}>{cell => <td class="border-t border-border px-4 py-2.5 text-foreground-subtle">{cell}</td>}</For>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </div>
      </Show>

      <Show when={props.widget.type === 'navbar'}>
        <div class={`flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-surface px-4 py-3 ${p().sticky ? 'sticky top-0 z-30' : ''}`}>
          <div class="flex items-center gap-2">
            <Show when={p().src}>
              <img src={p().src} alt={p().brand ?? ''} class="h-8 w-auto" />
            </Show>
            <span class="font-bold text-primary">{p().brand}</span>
          </div>
          <nav class="flex flex-wrap items-center gap-1">
            <For each={p().links ?? []}>
              {link => (
                <Button size="sm" variant="text" color="secondary" onClick={() => openLink(link.href, false, props.interactive)}>
                  {link.label}
                </Button>
              )}
            </For>
            <Show when={p().label}>
              <Button size="sm" color={p().color ?? 'primary'} onClick={() => openLink(p().href, p().newTab, props.interactive)}>
                {p().label}
              </Button>
            </Show>
          </nav>
        </div>
      </Show>

      <Show when={props.widget.type === 'breadcrumbs'}>
        <Breadcrumbs items={(p().links ?? []).map((link, index, all) => ({ title: link.label ?? '', href: link.href || undefined, active: index === all.length - 1 }))} />
      </Show>

      <Show when={props.widget.type === 'modal'}>
        <OverlayWidget widget={props.widget} interactive={props.interactive} kind="modal" />
      </Show>

      <Show when={props.widget.type === 'drawer'}>
        <OverlayWidget widget={props.widget} interactive={props.interactive} kind="drawer" />
      </Show>

      <Show when={props.widget.type === 'hero'}>
        <div class={`grid items-center gap-8 ${p().src ? 'sm:grid-cols-2' : ''}`}>
          <div class={`space-y-4 ${ALIGN[p().align ?? 'left']}`}>
            <h1 class="text-3xl sm:text-5xl font-bold leading-tight text-foreground">{p().title}</h1>
            <p class="text-lg text-foreground-subtle whitespace-pre-line">{p().text}</p>
            <div class={`flex flex-wrap gap-3 ${JUSTIFY[p().align ?? 'left']}`}>
              <Show when={p().label}>
                <Button size="lg" color={p().color ?? 'primary'} onClick={() => openLink(p().href, p().newTab, props.interactive)}>
                  {p().label}
                </Button>
              </Show>
              <Show when={p().secondaryLabel}>
                <Button size="lg" variant="outlined" color={p().color ?? 'primary'} onClick={() => openLink(p().secondaryHref, p().newTab, props.interactive)}>
                  {p().secondaryLabel}
                </Button>
              </Show>
            </div>
          </div>
          <Show when={p().src}>
            <img src={p().src} alt={p().alt ?? ''} class="w-full rounded-2xl object-cover" />
          </Show>
        </div>
      </Show>

      <Show when={props.widget.type === 'cta'}>
        <div class={`rounded-2xl bg-primary-soft p-8 ${ALIGN[p().align ?? 'center']}`}>
          <h3 class="text-2xl font-bold text-foreground">{p().title}</h3>
          <p class="mt-2 text-foreground-subtle whitespace-pre-line">{p().text}</p>
          <Show when={p().label}>
            <div class={`mt-5 flex ${JUSTIFY[p().align ?? 'center']}`}>
              <Button size="lg" color={p().color ?? 'primary'} onClick={() => openLink(p().href, p().newTab, props.interactive)}>
                {p().label}
              </Button>
            </div>
          </Show>
        </div>
      </Show>

      <Show when={props.widget.type === 'stats'}>
        <div class={`grid gap-6 ${GALLERY_COLUMNS[p().columns ?? 3]}`}>
          <For each={p().values ?? []}>
            {stat => (
              <div class="text-center">
                <p class="text-3xl sm:text-4xl font-bold text-primary">{stat.value}</p>
                <p class="mt-1 text-sm text-foreground-subtle">{stat.label}</p>
              </div>
            )}
          </For>
        </div>
      </Show>

      <Show when={props.widget.type === 'pricing'}>
        <div class={`grid gap-4 ${GALLERY_COLUMNS[p().columns ?? 3]}`}>
          <For each={p().values ?? []}>
            {plan => (
              <div class={`flex h-full flex-col rounded-2xl border p-6 ${plan.highlight ? 'border-primary bg-primary-soft/30' : 'border-border bg-surface'}`}>
                <p class="text-sm font-semibold uppercase tracking-wide text-foreground-subtle">{plan.label}</p>
                <p class="mt-2 text-2xl font-bold text-foreground">{plan.value}</p>
                <p class="text-xs text-foreground-subtle">{plan.suffix}</p>
                <ul class="mt-4 flex-1 space-y-2">
                  <For each={plan.features ?? []}>
                    {feature => (
                      <li class="flex items-start gap-2 text-sm text-foreground-subtle">
                        <Icon icon="material-symbols-light:check-circle-outline" size={18} class="mt-0.5 shrink-0 text-primary" />
                        {feature}
                      </li>
                    )}
                  </For>
                </ul>
                <Show when={p().label}>
                  <div class="mt-5">
                    <Button fullWidth color={p().color ?? 'primary'} variant={plan.highlight ? 'contained' : 'soft'} onClick={() => openLink(p().href, p().newTab, props.interactive)}>
                      {p().label}
                    </Button>
                  </div>
                </Show>
              </div>
            )}
          </For>
        </div>
      </Show>

      <Show when={props.widget.type === 'steps'}>
        <ol class="space-y-5">
          <For each={p().items ?? []}>
            {(step, index) => (
              <li class="flex gap-4">
                <span class="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary font-semibold text-white">{index() + 1}</span>
                <div>
                  <h4 class="font-semibold text-foreground">{step.title}</h4>
                  <p class="text-sm text-foreground-subtle whitespace-pre-line">{step.text}</p>
                </div>
              </li>
            )}
          </For>
        </ol>
      </Show>

      <Show when={props.widget.type === 'team'}>
        <div class={`grid gap-6 ${GALLERY_COLUMNS[p().columns ?? 3]}`}>
          <For each={p().quotes ?? []}>
            {person => (
              <div class="text-center">
                <Show
                  when={person.avatar}
                  fallback={
                    <span class="mx-auto flex size-20 items-center justify-center rounded-full bg-primary-soft text-2xl font-semibold text-primary">
                      {(person.author ?? '?').trim().charAt(0).toUpperCase()}
                    </span>
                  }
                >
                  <img src={person.avatar} alt={person.author ?? ''} class="mx-auto size-20 rounded-full object-cover" />
                </Show>
                <p class="mt-3 font-semibold text-foreground">{person.author}</p>
                <p class="text-sm text-foreground-subtle">{person.role}</p>
                <Show when={person.text}>
                  <p class="mt-1 text-xs text-foreground-subtle whitespace-pre-line">{person.text}</p>
                </Show>
              </div>
            )}
          </For>
        </div>
      </Show>

      <Show when={props.widget.type === 'logos'}>
        <div class="space-y-4">
          <Show when={p().title}>
            <p class="text-center text-sm font-medium uppercase tracking-wide text-foreground-subtle">{p().title}</p>
          </Show>
          <Show
            when={(p().images ?? []).length > 0}
            fallback={<div class="rounded-xl border border-dashed border-border py-8 text-center text-sm text-foreground-subtle">Añade los logos de tus clientes.</div>}
          >
            <div class="flex flex-wrap items-center justify-center gap-8 opacity-80">
              <For each={p().images ?? []}>{logo => <img src={logo.src} alt={logo.alt ?? ''} class="h-10 w-auto object-contain" />}</For>
            </div>
          </Show>
        </div>
      </Show>

      <Show when={props.widget.type === 'quote'}>
        {/* La barra sólo acompaña cuando la cita va a la izquierda: centrada,
            quedaba una raya suelta en el margen. */}
        <blockquote class={`${p().align === 'left' ? 'border-l-4 border-primary pl-5' : ''} ${ALIGN[p().align ?? 'center']}`}>
          <Show when={p().align !== 'left'}>
            <Icon icon="material-symbols-light:format-quote" size={34} class={`text-primary ${p().align === 'right' ? 'ml-auto' : 'mx-auto'}`} />
          </Show>
          <p class="text-xl italic text-foreground whitespace-pre-line">“{p().text}”</p>
          <Show when={p().title}>
            <footer class="mt-2 text-sm text-foreground-subtle">— {p().title}</footer>
          </Show>
        </blockquote>
      </Show>

      <Show when={props.widget.type === 'contact'}>
        <div class={`grid gap-5 ${GALLERY_COLUMNS[p().columns ?? 3]}`}>
          <For each={p().items ?? []}>
            {item => (
              <div class="flex items-start gap-3">
                <Show when={item.icon}>
                  <Icon icon={item.icon!} size={26} class="shrink-0 text-primary" />
                </Show>
                <div>
                  <p class="text-sm font-semibold text-foreground">{item.title}</p>
                  <p class="text-sm text-foreground-subtle whitespace-pre-line">{item.text}</p>
                </div>
              </div>
            )}
          </For>
        </div>
      </Show>

      <Show when={props.widget.type === 'social'}>
        <div class={`flex flex-wrap gap-2 ${JUSTIFY[p().align ?? 'left']}`}>
          <For each={p().links ?? []}>
            {link => (
              <Button
                size="md"
                variant="soft"
                color={p().color ?? 'secondary'}
                tooltip={link.label}
                icon={link.icon || 'material-symbols-light:link'}
                onClick={() => openLink(link.href, true, props.interactive)}
              />
            )}
          </For>
        </div>
      </Show>

      <Show when={props.widget.type === 'map'}>
        <div class="overflow-hidden rounded-xl border border-border" style={{ height: `${p().embedHeight ?? 320}px` }}>
          <iframe title="Mapa" class="h-full w-full" loading="lazy" src={`https://www.google.com/maps?q=${encodeURIComponent(p().text ?? '')}&output=embed`} />
        </div>
      </Show>

      <Show when={props.widget.type === 'embed'}>
        <Show when={p().url} fallback={<div class="rounded-xl border border-dashed border-border py-10 text-center text-sm text-foreground-subtle">Pega la dirección que quieres incrustar.</div>}>
          <iframe title={p().title ?? 'Contenido incrustado'} src={p().url} class="w-full rounded-xl border border-border" style={{ height: `${p().embedHeight ?? 420}px` }} />
        </Show>
      </Show>

      <Show when={props.widget.type === 'code'}>
        <div class="overflow-hidden rounded-xl border border-border bg-slate-900">
          <Show when={p().title}>
            <p class="border-b border-slate-700 px-4 py-2 text-xs font-medium text-slate-300">{p().title}</p>
          </Show>
          <pre class="overflow-x-auto p-4 text-sm text-slate-100">
            <code>{p().code}</code>
          </pre>
        </div>
      </Show>

      <Show when={props.widget.type === 'progress'}>
        <div class="space-y-4">
          <For each={p().values ?? []}>
            {item => (
              <div>
                <div class="mb-1 flex justify-between text-sm">
                  <span class="text-foreground">{item.label}</span>
                  <span class="text-foreground-subtle">{item.value}%</span>
                </div>
                <div class="h-2 overflow-hidden rounded-full bg-surface-muted">
                  <div class="h-full rounded-full bg-primary" style={{ width: `${Math.min(100, Math.max(0, Number(item.value) || 0))}%` }} />
                </div>
              </div>
            )}
          </For>
        </div>
      </Show>

      <Show when={props.widget.type === 'buttons'}>
        <div class={`flex flex-wrap gap-3 ${JUSTIFY[p().align ?? 'left']}`}>
          <For each={p().links ?? []}>
            {(link, index) => (
              <Button
                size={p().buttonSize ?? 'md'}
                color={p().color ?? 'primary'}
                variant={index() === 0 ? 'contained' : 'outlined'}
                onClick={() => openLink(link.href, p().newTab, props.interactive)}
              >
                {link.label}
              </Button>
            )}
          </For>
        </div>
      </Show>

      <Show when={props.widget.type === 'icon'}>
        <div class={`flex ${JUSTIFY[p().align ?? 'center']}`}>
          <Icon icon={p().icon || 'material-symbols-light:star'} size={p().maxWidth ?? 48} class={TONE[p().tone ?? 'primary'] ?? 'text-primary'} />
        </div>
      </Show>

      <Show when={props.widget.type === 'footer'}>
        <div class="space-y-4 border-t border-border pt-6">
          <div class="flex flex-wrap items-center justify-between gap-4">
            <span class="font-bold text-primary">{p().brand}</span>
            <nav class="flex flex-wrap gap-1">
              <For each={p().links ?? []}>
                {link => (
                  <Button size="sm" variant="text" color="secondary" onClick={() => openLink(link.href, false, props.interactive)}>
                    {link.label}
                  </Button>
                )}
              </For>
            </nav>
          </div>
          <p class="text-center text-xs text-foreground-subtle">{p().text}</p>
        </div>
      </Show>

      <Show when={props.widget.type === 'divider'}>
        <hr class="border-border" />
      </Show>

      <Show when={props.widget.type === 'spacer'}>
        <div class={SPACER[p().height ?? 'md']} />
      </Show>

      <Show when={props.widget.type === 'html'}>
        {/* Es HTML que escribe quien administra la página en su propio panel. */}
        <div innerHTML={p().code ?? ''} />
      </Show>
    </>
  )
}

export interface PageRendererPropsType {
  content: PageContentType
  /** En el editor los enlaces y envíos no se ejecutan. */
  interactive?: boolean
  /** Envoltorio de cada widget: lo usa el lienzo para seleccionar y arrastrar. */
  wrapWidget?: (widget: WidgetType, column: ColumnType, section: SectionType, rendered: () => JSX.Element) => JSX.Element
  wrapColumn?: (column: ColumnType, section: SectionType, rendered: () => JSX.Element) => JSX.Element
  wrapSection?: (section: SectionType, rendered: () => JSX.Element) => JSX.Element
  /**
   * Reemplaza el contenido de una columna. El lienzo del editor lo usa para
   * intercalar sus zonas de soltado entre widget y widget.
   */
  renderColumnBody?: (column: ColumnType, section: SectionType) => JSX.Element
}

const PageRenderer: Component<PageRendererPropsType> = props => {
  const interactive = () => props.interactive !== false

  const renderWidget = (widget: WidgetType, column: ColumnType, section: SectionType) => {
    const view = () => <WidgetPreview widget={widget} interactive={interactive()} />
    return props.wrapWidget ? props.wrapWidget(widget, column, section, view) : view()
  }

  const renderColumn = (column: ColumnType, section: SectionType) => {
    const view = () =>
      props.renderColumnBody ? (
        props.renderColumnBody(column, section)
      ) : (
        <div class="space-y-4">
          <For each={column.widgets}>{widget => <>{renderWidget(widget, column, section)}</>}</For>
        </div>
      )
    return props.wrapColumn ? props.wrapColumn(column, section, view) : view()
  }

  const renderSection = (section: SectionType) => {
    const style = section.style ?? {}
    const view = () => (
      <div
        class={`${BACKGROUND[style.background ?? 'none']} ${PADDING_Y[style.paddingY ?? 'lg']} bg-cover bg-center`}
        style={style.backgroundImage ? { 'background-image': `url(${style.backgroundImage})` } : undefined}
      >
        <div class={`mx-auto px-4 sm:px-6 ${SECTION_WIDTH[style.width ?? 'boxed']}`}>
          <div class={`grid grid-cols-1 sm:grid-cols-12 ${GAP[style.gap ?? 'md']} ${style.reverseOnMobile ? 'flex-col-reverse' : ''}`}>
            <For each={section.columns}>{column => <div class={COLUMN_SPAN[column.span] ?? COLUMN_SPAN[12]}>{renderColumn(column, section)}</div>}</For>
          </div>
        </div>
      </div>
    )
    return props.wrapSection ? props.wrapSection(section, view) : view()
  }

  return <For each={props.content.sections}>{section => <>{renderSection(section)}</>}</For>
}

export default PageRenderer
